// :)
/**
 * Shared helpers for repo connection during install and `synkro link`.
 *
 * Two paths:
 *   1. Local git repo — detect from `git remote get-url origin`, any provider
 *   2. GitHub OAuth — browser flow, list repos, interactive picker
 */
import { execSync, execFileSync } from 'node:child_process';
import { readdirSync } from 'node:fs';
import { createServer } from 'node:http';
import { createInterface } from 'node:readline';
import { createProject, listProjects } from '../api/projects.js';
import { listAccessibleRepos } from '../installer/githubSetup.js';

const RAW_WEB_AUTH_URL = process.env.SYNKRO_WEB_AUTH_URL;
const SYNKRO_WEB_AUTH_URL = (RAW_WEB_AUTH_URL && /^https?:\/\//.test(RAW_WEB_AUTH_URL))
  ? RAW_WEB_AUTH_URL
  : 'https://app.synkro.sh';
const GITHUB_PORT = 8101;

function detectGitRepo(): { fullName: string; shortName: string } | null {
  try {
    const remoteUrl = execSync('git remote get-url origin', { encoding: 'utf-8', timeout: 5000, stdio: ['pipe', 'pipe', 'pipe'] }).trim();
    // Match any git host — github, gitlab, bitbucket, self-hosted, etc.
    const sshMatch = remoteUrl.match(/^git@[^:]+:(.+?)(?:\.git)?$/);
    const httpMatch = remoteUrl.match(/^https?:\/\/[^/]+\/(.+?)(?:\.git)?$/);
    const match = sshMatch || httpMatch;
    if (!match) return null;
    const fullName = match[1];
    return { fullName, shortName: fullName.split('/').pop() || fullName };
  } catch { return null; }
}

function detectSubdirRepos(): Array<{ fullName: string; shortName: string }> {
  try {
    const entries = readdirSync('.', { withFileTypes: true });
    const repos: Array<{ fullName: string; shortName: string }> = [];
    for (const entry of entries) {
      if (!entry.isDirectory() || entry.name.startsWith('.')) continue;
      try {
        const remoteUrl = execFileSync('git', ['-C', entry.name, 'remote', 'get-url', 'origin'], {
          encoding: 'utf-8',
          timeout: 5000,
          stdio: ['pipe', 'pipe', 'pipe'],
        }).trim();
        const sshMatch = remoteUrl.match(/^git@[^:]+:(.+?)(?:\.git)?$/);
        const httpMatch = remoteUrl.match(/^https?:\/\/[^/]+\/(.+?)(?:\.git)?$/);
        const match = sshMatch || httpMatch;
        if (match) {
          const fullName = match[1];
          repos.push({ fullName, shortName: fullName.split('/').pop() || fullName });
        }
      } catch { /* not a git repo or no remote */ }
    }
    return repos;
  } catch { return []; }
}

function ask(rl: ReturnType<typeof createInterface>, question: string): Promise<string> {
  return new Promise((resolve) => rl.question(question, resolve));
}

function waitForGithubToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    const server = createServer((req, res) => {
      if (req.method === 'OPTIONS') {
        res.writeHead(204, {
          'Access-Control-Allow-Origin': SYNKRO_WEB_AUTH_URL,
          'Access-Control-Allow-Methods': 'POST, OPTIONS',
          'Access-Control-Allow-Headers': 'Content-Type',
        });
        res.end();
        return;
      }

      if (req.url !== '/auth' || req.method !== 'POST') {
        res.writeHead(404);
        res.end();
        return;
      }

      let body = '';
      req.on('data', (chunk) => { body += chunk; });
      req.on('end', () => {
        try {
          const parsed = JSON.parse(body);
          if (!parsed.github_token) {
            res.writeHead(400, {
              'Content-Type': 'application/json',
              'Access-Control-Allow-Origin': SYNKRO_WEB_AUTH_URL,
            });
            res.end(JSON.stringify({ error: 'missing github_token' }));
            return;
          }
          res.writeHead(200, {
            'Content-Type': 'application/json',
            'Access-Control-Allow-Origin': SYNKRO_WEB_AUTH_URL,
          });
          res.end(JSON.stringify({ ok: true }));
          setTimeout(() => server.close(), 200);
          resolve(parsed.github_token);
        } catch {
          res.writeHead(400, { 'Content-Type': 'application/json' });
          res.end(JSON.stringify({ error: 'invalid json' }));
        }
      });
    });

    server.on('error', (err: NodeJS.ErrnoException) => {
      if (err.code === 'EADDRINUSE') {
        reject(new Error(`Port ${GITHUB_PORT} is in use. Close other processes and try again.`));
      } else {
        reject(err);
      }
    });

    server.listen(GITHUB_PORT);
  });
}

function openBrowser(url: string): void {
  const { execFile } = require('node:child_process');
  const plat = process.platform;
  const cb = (err: Error | null) => {
    if (err) console.log(`  Open this URL manually: ${url}`);
  };
  if (plat === 'darwin') execFile('open', [url], cb);
  else if (plat === 'win32') execFile('cmd', ['/c', 'start', '', url], cb);
  else execFile('xdg-open', [url], cb);
}

async function connectGithubAndSelectRepos(): Promise<Array<{ full_name: string }>> {
  const url = `${SYNKRO_WEB_AUTH_URL}/cli-github?port=${GITHUB_PORT}`;
  console.log('  Opening browser for GitHub authorization...');
  openBrowser(url);

  console.log('  Waiting for GitHub authorization...');
  const ghToken = await waitForGithubToken();
  console.log('  ✓ GitHub connected\n');

  const repos = await listAccessibleRepos({ token: ghToken });
  if (repos.length === 0) {
    console.log('  No accessible repos found on GitHub.');
    return [];
  }

  console.log(`  Found ${repos.length} repos:\n`);
  repos.forEach((r: any, i: number) => {
    console.log(`    ${String(i + 1).padStart(3)}. ${r.full_name}`);
  });
  console.log();

  const rl = createInterface({ input: process.stdin, output: process.stdout });
  try {
    const selection = await ask(rl, '  Select repos (comma-separated numbers, e.g. 1,3,5): ');
    const indices = selection
      .split(',')
      .map((s) => parseInt(s.trim(), 10) - 1)
      .filter((n) => !isNaN(n) && n >= 0 && n < repos.length);

    if (indices.length === 0) {
      console.log('  No repos selected.');
      return [];
    }

    return indices.map((i) => ({ full_name: repos[i].full_name }));
  } finally {
    rl.close();
  }
}

export async function promptRepoConnection(opts?: { linkRepo?: boolean }): Promise<void> {
  const localRepo = detectGitRepo();
  const subdirRepos = !localRepo ? detectSubdirRepos() : [];

  if (opts?.linkRepo && localRepo) {
    console.log('Connect repos to Synkro:\n');
    try {
      const existing = await listProjects();
      const alreadyLinked = existing.some((p: any) =>
        p.repos?.some((r: any) => r.full_name === localRepo.fullName),
      );
      if (!alreadyLinked) {
        await createProject(localRepo.shortName, [{ full_name: localRepo.fullName }]);
        console.log(`  ✓ Created project "${localRepo.shortName}" linked to ${localRepo.fullName}`);
      } else {
        console.log(`  ✓ ${localRepo.fullName} is already linked to a Synkro project.`);
      }
    } catch (err) {
      console.warn(`  ⚠ Could not link repo: ${(err as Error).message}`);
    }
    console.log();
    return;
  }

  const rl = createInterface({ input: process.stdin, output: process.stdout });

  try {
    console.log('Connect repos to Synkro:\n');

    let existingFullNames = new Set<string>();
    if (subdirRepos.length > 0) {
      try {
        const existing = await listProjects();
        existingFullNames = new Set(
          existing.flatMap((p: any) => (p.repos || []).map((r: any) => r.full_name)),
        );
      } catch {}
    }

    if (subdirRepos.length > 0) {
      console.log(`  Found ${subdirRepos.length} repo(s) in this directory:\n`);
      subdirRepos.forEach((r, i) => {
        const linked = existingFullNames.has(r.fullName);
        console.log(`    ${String(i + 1).padStart(3)}. ${r.fullName}${linked ? '  ✓ linked' : ''}`);
      });
      const ghIdx = subdirRepos.length + 1;
      const skipIdx = subdirRepos.length + 2;
      console.log(`    ${String(ghIdx).padStart(3)}. Connect GitHub to select repos`);
      console.log(`    ${String(skipIdx).padStart(3)}. Skip for now`);
      console.log();

      const selection = await ask(rl, '  Select repos to link (comma-separated numbers, e.g. 1,3): ');
      const nums = selection.split(',').map((s) => parseInt(s.trim(), 10)).filter((n) => !isNaN(n));
      console.log();
      rl.close();

      if (nums.includes(ghIdx)) {
        const selectedRepos = await connectGithubAndSelectRepos();
        if (selectedRepos.length > 0) {
          const newRepos = selectedRepos.filter((r) => !existingFullNames.has(r.full_name));
          if (newRepos.length === 0) {
            console.log('  ✓ All selected repos are already linked.');
          } else {
            const projectName = newRepos.length === 1
              ? newRepos[0].full_name.split('/').pop() || 'Project'
              : 'Multi-Repo Project';
            await createProject(projectName, newRepos);
            console.log(`  ✓ Linked ${newRepos.length} repo(s) to project "${projectName}"`);
          }
        }
      } else if (nums.includes(skipIdx) || nums.length === 0) {
        console.log('  Skipped. Run `synkro link` later to connect repos.');
      } else {
        const repoIndices = nums.map((n) => n - 1).filter((n) => n >= 0 && n < subdirRepos.length);
        const toLink = repoIndices.map((i) => subdirRepos[i]).filter((r) => !existingFullNames.has(r.fullName));
        if (toLink.length === 0) {
          console.log('  ✓ All selected repos are already linked.');
        } else {
          for (const repo of toLink) {
            try {
              await createProject(repo.shortName, [{ full_name: repo.fullName }]);
              console.log(`  ✓ Created project "${repo.shortName}" linked to ${repo.fullName}`);
            } catch (err) {
              console.warn(`  ⚠ Could not link ${repo.fullName}: ${(err as Error).message}`);
            }
          }
        }
      }
    } else {
      const options: string[] = [];
      if (localRepo) {
        options.push(`Link this repo (${localRepo.fullName})`);
      }
      options.push('Connect GitHub to select repos');
      options.push('Skip for now');

      options.forEach((opt, i) => {
        console.log(`  ${i + 1}. ${opt}`);
      });
      console.log();

      const choice = await ask(rl, '  Choose (number): ');
      const choiceNum = parseInt(choice.trim(), 10);
      console.log();
      rl.close();

      const localIdx = localRepo ? 1 : -1;
      const githubIdx = localRepo ? 2 : 1;
      const skipIdx = localRepo ? 3 : 2;

      if (choiceNum === localIdx && localRepo) {
        try {
          const existing = await listProjects();
          const alreadyLinked = existing.some((p: any) =>
            p.repos?.some((r: any) => r.full_name === localRepo.fullName),
          );
          if (!alreadyLinked) {
            await createProject(localRepo.shortName, [{ full_name: localRepo.fullName }]);
            console.log(`  ✓ Created project "${localRepo.shortName}" linked to ${localRepo.fullName}`);
          } else {
            console.log(`  ✓ ${localRepo.fullName} is already linked to a Synkro project.`);
          }
        } catch (err) {
          console.warn(`  ⚠ Could not link repo: ${(err as Error).message}`);
        }
      } else if (choiceNum === githubIdx) {
        const selectedRepos = await connectGithubAndSelectRepos();
        if (selectedRepos.length > 0) {
          try {
            const existing = await listProjects();
            const linkedNames = new Set(
              existing.flatMap((p: any) => (p.repos || []).map((r: any) => r.full_name)),
            );
            const newRepos = selectedRepos.filter((r) => !linkedNames.has(r.full_name));

            if (newRepos.length === 0) {
              console.log('  ✓ All selected repos are already linked.');
            } else {
              const projectName = newRepos.length === 1
                ? newRepos[0].full_name.split('/').pop() || 'Project'
                : 'Multi-Repo Project';
              await createProject(projectName, newRepos);
              console.log(`  ✓ Linked ${newRepos.length} repo(s) to project "${projectName}"`);
            }
          } catch (err) {
            console.warn(`  ⚠ Could not link repos: ${(err as Error).message}`);
          }
        }
      } else if (choiceNum === skipIdx) {
        console.log('  Skipped. Run `synkro link` later to connect repos.');
      } else {
        console.log('  Invalid choice. Skipping repo connection.');
      }
    }
  } catch {
    rl.close();
  }
  console.log();
}
